import { Link } from 'react-router-dom';
import { Mail, MapPin, ArrowRight, Linkedin, Facebook, Twitter } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Employers', path: '/employers' },
    { name: 'Job Seekers', path: '/job-seekers' },
    { name: 'Industries', path: '/industries' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    'Temporary Staffing',
    'Contract Staffing',
    'Permanent Placement',
    'Payroll Services',
    'HR Support',
  ];

  const industries = [
    'Warehousing & Logistics',
    'Manufacturing',
    'Transportation',
    'Administrative & Office',
    'Customer Service',
  ];

  return (
    <footer className="footer" data-testid="main-footer">
      <div className="container-custom">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 pb-12 border-b border-white/10">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="8" fill="#1E90FF"/>
                <path d="M12 20C12 15.5817 15.5817 12 20 12V12C24.4183 12 28 15.5817 28 20V28H20C15.5817 28 12 24.4183 12 20V20Z" fill="white"/>
                <path d="M20 12L28 20L20 28" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="16" cy="20" r="2" fill="#0B1F3A"/>
              </svg>
              <div>
                <span className="font-['Chivo'] font-black text-xl text-white tracking-tight">FleetHire</span>
                <span className="block text-xs text-[#94A3B8] tracking-wide">Staffing Solutions</span>
              </div>
            </Link>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
              Fast. Reliable. Workforce Solutions. Connecting businesses with reliable talent across the Greater Toronto Area.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1E90FF] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} strokeWidth={1.5} />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1E90FF] transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={18} strokeWidth={1.5} />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1E90FF] transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Chivo'] font-bold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-[#1E90FF] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-['Chivo'] font-bold text-white mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-[#94A3B8] text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Chivo'] font-bold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:info@fleethire.ca" 
                  className="flex items-start gap-3 text-[#94A3B8] text-sm hover:text-[#1E90FF] transition-colors"
                >
                  <Mail size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <span>info@fleethire.ca</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-[#94A3B8] text-sm">
                  <MapPin size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <span>30 Kennedy Rd S<br />Brampton, ON L6W 3E2</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#64748B] text-sm">
            &copy; {currentYear} FleetHire Staffing Solutions. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy" className="text-[#64748B] hover:text-[#1E90FF] transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#64748B] hover:text-[#1E90FF] transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
